import { get, isEmpty } from 'lodash';
import {
    LOG_OUT_DONE,
    LOG_OUT_ERROR,
    LOGIN_TYPE,
    ANALYTICS_ATTRIBUTES,
    SET_LOGIN_FETCHING,
    SET_LOGIN_SESSION,
    REMOVE_LOGIN_SESSION,
    HAS_SESSION_ALREADY,
    SET_TPC_ENABLED
} from './login.constants';
import { externalLoginOptionType } from 'modules/ubisoft-connect-service/constants';

// types
import { IGetProfilesResponse } from 'modules/ubisoft-connect-service/types';
import { IUserInfo, ILoginSession, ILoginProfile } from 'types/session';
import { Dispatch } from 'types/dispatch';

// service
import { prepareProfilePlatforms } from './login.service';
import ubisoftConnectService from 'modules/ubisoft-connect-service';
import AnalyticsService from 'modules/analytics-provider/analytics-provider.service';

// constants
import { UBISOFT_CONNECT_PLATFORM_TYPES } from 'modules/ubisoft-connect-service/constants';
import { WEBSITE_MODE } from '@sitegen/common/constants/config';
import { IUbiOverlayWidget } from 'modules/ubisoft-overlay-service/types';

export const setLoginFetching = (payload: boolean) => ({ type: SET_LOGIN_FETCHING, payload });

export const setLoginSession = (payload: ILoginSession) => ({ type: SET_LOGIN_SESSION, payload });

export const removeLoginSession = () => ({ type: REMOVE_LOGIN_SESSION });

export const broadcastHasSession = () => ({ type: HAS_SESSION_ALREADY });

export const setTPCEnabled = (payload: boolean) => ({ type: SET_TPC_ENABLED, payload });

// utils
export const trackLogin = (isAnalyticsEnabled: boolean): void => {
    if (isAnalyticsEnabled) {
        AnalyticsService.sendAccountEvent({
            action: [
                ANALYTICS_ATTRIBUTES.location,
                ANALYTICS_ATTRIBUTES.category,
                ANALYTICS_ATTRIBUTES.action.login
            ].join(' - '),
            loggedIn: true,
            loggedOut: false
        });
    }
};

const requestLoginSessionCallback = (dispatch: Dispatch, getState: Function): Promise<void> => {
    /* TODO: https://gitlab-ncsa.ubisoft.org/Sitegen/sitegen-main/issues/102  */
    /* eslint-disable-next-line*/
    const state: any = getState();
    const platformSelection: string[] = state.config.platformSelection;
    const mode: string = state.config.mode;
    const hasTPCEnabled: boolean = get(state, 'login.hasTPCEnabled');
    const activeUserId: string = get(state, 'login.loginSession.UbisoftConnectProfile.user.userId');
    dispatch(setLoginFetching(true));

    return ubisoftConnectService
        .getTicket(hasTPCEnabled)
        .then(getTicketResult => {
            const { ticket, sessionId, userId } = getTicketResult;

            return ubisoftConnectService
                .getProfiles({
                    ticket,
                    sessionId,
                    userId
                })
                .then((getProfilesResult: IGetProfilesResponse) => {
                    const type: LOGIN_TYPE = getLogInType(userId, activeUserId);
                    const session: ILoginSession = {
                        sessionId,
                        isUbisoftConnectAuthenticated: true,
                        UbisoftConnectTicket: ticket,
                        UbisoftConnectProfile: {
                            user: getProfilesResult.payload.profiles.find(
                                (item: IUserInfo) => item.platformType === UBISOFT_CONNECT_PLATFORM_TYPES.UPLAY
                            )
                        } as ILoginProfile,
                        UbisoftConnectProfiles: prepareProfilePlatforms(getProfilesResult.payload.profiles, platformSelection),
                        type
                    };

                    dispatch(setLoginSession(session));

                    if (
                        WEBSITE_MODE.EMBEDDED === mode &&
                        !isEmpty( (window as Window & typeof globalThis & { overlayWidget: IUbiOverlayWidget }).overlayWidget)
                    ) {
                        (window as Window & typeof globalThis & { overlayWidget: IUbiOverlayWidget }).overlayWidget.reInit();
                    }

                    trackLogin(state.config.isAnalyticsEnabled);
                });
        })
        .finally(() => {
            dispatch(setLoginFetching(false));
        });
};

const getLogInType = (userId: string, activeUserId: string): LOGIN_TYPE => {
    if (userId === activeUserId) {
        return LOGIN_TYPE.CONFIRM_ACCOUNT;
    }

    if (userId !== activeUserId && activeUserId) {
        return LOGIN_TYPE.RESELECT_ACCOUNT;
    }

    return LOGIN_TYPE.NEW_LOGIN;
};

/** Check if login session is already available in "Connect" */
export const requestLoginSession = () => (dispatch: Dispatch, getState: Function) => {
    const hasTPCEnabled = get(getState(), 'login.hasTPCEnabled');
    if (hasTPCEnabled) {
        return requestLoginSessionCallback(dispatch, getState); 
    }
    return removeLoginSession();
};

/** Provide callback for "Connect" service which emits actions */
export const setUpLoginSessionCallback = () => (dispatch: Dispatch, getState: Function): void => {
    ubisoftConnectService.registerLoginCallback((): Promise<void> => {
        return requestLoginSessionCallback(dispatch, getState);
    });
};

export const logOut = () => (dispatch: Dispatch, getState: Function) => {
    const state = getState();
    const { login: { loginSession: { UbisoftConnectTicket, sessionId } , hasTPCEnabled } } = state;
    const internalBeta = get(state, 'config.betaRegistration.internalBeta', false);

    let adminConnectLogoutPromise = Promise.resolve();

    if (internalBeta) {
        adminConnectLogoutPromise = new Promise(resolve => {
            window['AdminConnect'].Logout(resolve);
        });
    }

    Promise.all([
        adminConnectLogoutPromise,
        ubisoftConnectService.logout(UbisoftConnectTicket, sessionId)
    ]).then(() => {
        dispatch({
            type: LOG_OUT_DONE
        });

        AnalyticsService.sendAccountEvent({
            action: [
                ANALYTICS_ATTRIBUTES.location,
                ANALYTICS_ATTRIBUTES.category,
                ANALYTICS_ATTRIBUTES.action.logout
            ].join(' - '),
            loggedIn: false,
            loggedOut: true
        });
    }).finally(
        () => {
            if (!hasTPCEnabled) {
                window.localStorage.clear();
                window.location.reload();
            }
        }
    )
        .catch(() => {
            dispatch({
                type: LOG_OUT_ERROR
            });
        });
};

export const openLoginPopup = (payload: externalLoginOptionType) => (
    _dispatch: Dispatch,
    getState: Function
) => {
    const {
        login: { hasTPCEnabled },
        language: { activeLanguageCode }
    } = getState();
    ubisoftConnectService.openLoginPopUp(activeLanguageCode, payload, 'center', hasTPCEnabled);
};

export type SetLoginSession = ReturnType<typeof setLoginSession>;

export type SetTpcEnabled = ReturnType<typeof setTPCEnabled>;

export type Actions = SetLoginSession | ReturnType<typeof setLoginFetching> | ReturnType<typeof removeLoginSession> | SetTpcEnabled;
